import { type Notification, NotificationType } from '@lov/shared';

/**
 * Resolves the target route for a notification.
 * Uses actionUrl if present, otherwise derives from type + data.
 *
 * Pass userRole to rewrite customer-facing booking URLs to vendor routes
 * when the viewer is a VENDOR or VENDOR_STAFF.
 */
export function getNotificationRoute(notification: Notification, userRole?: string): string | null {
  // Prefer explicit actionUrl
  let route: string | null = notification.actionUrl || null;

  // Fallback: derive from notification type + data
  if (!route) {
    const data = notification.data || {};
    const raw = data.bookingId;
    const bookingId = typeof raw === 'string' ? raw : undefined;

    switch (notification.type) {
      case NotificationType.BOOKING_CONFIRMATION:
      case NotificationType.BOOKING_REMINDER:
      case NotificationType.BOOKING_CANCELLED:
      case NotificationType.PAYMENT_SUCCESS:
      case NotificationType.PAYMENT_FAILED:
      case NotificationType.REFUND_INITIATED:
      case NotificationType.REFUND_COMPLETED:
      case NotificationType.REVIEW_REQUEST:
        route = bookingId ? `/bookings/${bookingId}` : null;
        break;
      case NotificationType.VENDOR_NEW_BOOKING:
        route = bookingId ? `/vendor/bookings/${bookingId}` : null;
        break;
      case NotificationType.VENDOR_APPROVAL:
        route = '/vendor/dashboard';
        break;
      case NotificationType.PAYOUT_SUCCESS:
      case NotificationType.PAYOUT_FAILURE:
        route = '/vendor/payouts';
        break;
      case NotificationType.SUBSCRIPTION_ACTIVATED:
      case NotificationType.SUBSCRIPTION_TRIAL_REMINDER:
      case NotificationType.SUBSCRIPTION_DOWNGRADED:
      case NotificationType.SUBSCRIPTION_EXPIRED:
        route = '/vendor/dashboard';
        break;
      case NotificationType.VENDOR_PENDING_CREDIT_ALERT:
      case NotificationType.VENDOR_DAILY_CREDIT_SUMMARY:
        route = '/vendor/dashboard';
        break;
      case NotificationType.ADMIN_NEW_BOOKING:
      case NotificationType.ADMIN_BOOKING_CANCELLED:
        route = bookingId ? `/admin/bookings/${bookingId}` : null;
        break;
      case NotificationType.PASSWORD_CHANGED:
      case NotificationType.SYSTEM_ANNOUNCEMENT:
      case NotificationType.PROMOTION:
      case NotificationType.SYSTEM:
      default:
        route = null;
    }
  }

  // Rewrite customer-facing booking URL for vendor/vendor-staff viewers
  if (route && (userRole === 'VENDOR' || userRole === 'VENDOR_STAFF')) {
    const match = route.match(/^\/bookings\/([a-f0-9-]+)$/);
    if (match) {
      route = `/vendor/bookings/${match[1]}`;
    }
  }

  return route;
}
